import { Container, Col, Row, BreadcrumbItem, Badge } from "reactstrap";
import { Link } from "react-router-dom";
import ContentTabs from "@repoflow/components/content-tabs";
import * as Notify from "@repoflow/form-schema/notify";
import * as ViewQL from "queries/view";
import { Mutation, Button, useSectionContext } from "@repoflow/components/section";
import { Component as Form } from "@repoflow/form-schema";
import * as appWidgets from "@shared/widgets/register";
import { QueryRunner } from "components/query-runner";

export const Sidebar = () => null;
export const Indice = () => <><BreadcrumbItem>Edit</BreadcrumbItem></>;

// A view's screen: the query, and what it returns.
//
// Two tabs and one Save, unlike a table's screen — a view IS its query, so there
// is nothing here with a second concurrency story. The Run tab executes whatever
// is in the editor RIGHT NOW rather than what was last saved, which is the only
// useful thing it could do: the point of a Run button beside an editor is to
// answer "does this work" before committing to it.
export const Content = () => {
  const { values, base } = useSectionContext();

  return (
    <>
      <Container fluid className="mt-4">
        <Row>
          <Col>
            <div className="float-right">
              <Link className="btn btn-danger" to={`${base}/remove`}>Remove</Link>
            </div>
            <h4 className="mb-0">
              {values.title}{" "}
              <Badge color="light" className="border align-middle">{values.viewId}</Badge>
            </h4>
            <small className="text-muted">
              {values.tables && values.tables.length
                ? <>reads {values.tables.join(", ")}</>
                : <>reads nothing yet</>}
            </small>
          </Col>
        </Row>
      </Container>

      <div className="mt-3" />

      <ContentTabs
        tabs={[
          { label: "Query", content: <QueryTab /> },
          { label: "Details", content: <DetailsTab /> },
        ]}
      />
    </>
  );
};

const QueryTab = () => {
  const { values, attributes } = useSectionContext();

  return (
    <Mutation
      initial={{
        title: values.title || "",
        description: values.description || "",
        query: values.query || "",
        params: values.params || [],
        tags: values.tags || [],
      }}
      target={{ $ref: "/Public/View/Update" }}
      mutation={ViewQL.edit}
      update={ViewQL.update(attributes)}
      onMutate={({ state: { values: v }, handler }) => {
        handler({ variables: { viewId: attributes.viewId, input: v } });
      }}
      onCompleted={() => Notify.success("Saved")}
      onError={(error) => {
        const message = String(error);
        // The server derives `tables` from the query, so a save is also the
        // moment a reference to a table that is gone is caught — and it names
        // which one, at the moment the person can still fix it.
        if (message.includes("view.query.table-missing")) {
          const id = message.split("view.query.table-missing:")[1] || "";
          Notify.error(`This query reads a table called "${id.trim()}", and there is no such table.`);
          return;
        }
        if (message.includes("view.query.expected-one-statement")) {
          Notify.error("A view is one query. Remove the second statement.");
          return;
        }
        if (message.includes("sql.ast.")) {
          Notify.error("This query uses something we cannot check for safety — a table-valued function or a variable in FROM.");
          return;
        }
        Notify.error(message);
      }}
    >
      {QueryTabHandler}
    </Mutation>
  );
};

const QueryTabHandler = ({ schema, state, mutate, target }) => {
  const { values, setValues } = state;

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          {/* `widget: "sql"` on the field is what makes this a CodeMirror
              editor — @repoflow/components/widgets/sql, registered in
              app/shared/widgets/register.

              The Run tab executes the DRAFT rather than the saved query, so
              every keystroke is mirrored out to the parent as well as into the
              form's own state. That is the whole point of a Run button beside an
              editor: it answers "does this work" before you commit to it. */}
          <Form
            schema={schema}
            target={target}
            values={values}
            widgets={appWidgets}
            onSubmit={() => mutate()}
            onChange={(vals) => setValues(vals)}
            config={{
              "/Public/View/Update": {
                fields: [{ property: "query", layout: "wide" }],
              },
            }}
          >
            <Button type="submit">Save</Button>
          </Form>

          {/* THE RUNNER LIVES UNDER THE EDITOR, NOT ON A TAB OF ITS OWN.
              It runs the DRAFT — what is in the editor right now, saved or not —
              which is the only useful thing a Run button beside an editor can
              do: it answers "does this work" before you commit to it. `values`
              is the FORM's state, so this is literally what is on screen —
              there is no second copy of the query to drift.

              On its own tab it could not. Switching tabs unmounts the body, so
              ContentTabs asks before leaving a dirty form (confirmDiscard), and
              a query you had just typed was exactly the state you most wanted to
              run. You had to save first to look — which is backwards, and is the
              coupling this arrangement removes. */}
          <hr className="mt-4" />
          <QueryRunner query={values.query || ""} params={[]} />
        </Col>
      </Row>
    </Container>
  );
};

const DetailsTab = () => {
  const { values, attributes } = useSectionContext();

  return (
    <Mutation
      initial={{
        title: values.title || "",
        description: values.description || "",
        query: values.query || "",
        params: values.params || [],
        tags: values.tags || [],
      }}
      target={{ $ref: "/Public/View/Update" }}
      mutation={ViewQL.edit}
      update={ViewQL.update(attributes)}
      onMutate={({ state: { values: v }, handler }) => {
        handler({ variables: { viewId: attributes.viewId, input: v } });
      }}
      onCompleted={() => Notify.success("Saved")}
      onError={(error) => Notify.error(error.toString())}
    >
      {DetailsTabHandler}
    </Mutation>
  );
};

const DetailsTabHandler = ({ schema, state, mutate, target }) => {
  const { values, setValues } = state;

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Form
            schema={schema}
            target={target}
            values={values}
            widgets={appWidgets}
            onSubmit={() => mutate()}
            onChange={(vals) => setValues(vals)}
            config={{
              "/Public/View/Update": {
                fields: [
                  { property: "title" },
                  { property: "description" },
                  // The bind parameters a shared view asks for. Declared as
                  // /Types/Param, the same shape a table's columns use, and
                  // bound to the query's `?` placeholders — never substituted
                  // into its text.
                  { property: "params", layout: "wide" },
                  { property: "tags" },
                ],
              },
            }}
          >
            <Button type="submit">Save</Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};
